import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../state/store";
import {
  resetMarkets,
  setGroupsConfigs,
  setMarketsError,
  setMarketsLoading,
  setUsersRefresh
} from "../../state/ducks";
import { selectAppTypesGroups, selectGroupsConfigs } from "../../state/selectors";

import { alignItems, appTypesNames, routePaths, strCode, tableDataStatuses } from "../../consts";
import {
  adminUserService,
  authService,
  groupsService,
  log,
  marketsService,
  notificationService,
  t,
  userChangeHistoryService,
  userGroupService,
  usersService
} from "../../services";
import { adminUserUtils, responseUtil, userUtil } from "../../utils";
import { withSection } from "../../hoc";
import {
  useAssignGroups,
  useDeleteUser,
  useFetchGroups,
  useResetPassword,
  useUpdateGroups
} from "../../apiHooks";

import {
  BackLink,
  FlexContainers,
  Heading4,
  PageContainer,
  Paragraph2,
  StatusIndicator,
  WrapperContainer
} from "../../components";
import { DeleteUser, NewUserForm, UserGroupsModal } from "../../containers";
import { UserLoginInfoTemplate } from "../../templates";

const EditUserPage = ({ isAdminUser }) => {
  const userToEdit = useSelector((store) => store.users?.userToEdit);
  const isMarketsLoading = useSelector((store) => store.markets?.loading);
  const history = useHistory();

  const [user, setUser] = useState();
  const [userChangeHistory, setUserChangeHistory] = useState();
  const [loading, setLoading] = useState(true);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(true);
  const [isAppTypesLoading, setIsAppTypesLoading] = useState(true);
  const [groupSettingsModalActive, setGroupSettingsModalActive] = useState(false);

  const { mutationCallback: saveGroups } = useUpdateGroups();
  const { fetchCallback: fetchGroups, refresh } = useFetchGroups();
  const { mutationCallback: assignGroups } = useAssignGroups();
  const { mutationCallback: deleteUser, loading: deleteUserLoading } = useDeleteUser();
  const { mutationCallback: resetPassword, loading: resetPasswordLoading } = useResetPassword();

  const userLoading = useMemo(
    () => deleteUserLoading || resetPasswordLoading,
    [deleteUserLoading, resetPasswordLoading]
  );

  const fetchUser = useCallback(async () => {
    setLoading(true);

    try {
      const userByEmail = isAdminUser
        ? await adminUserService.fetchAdminUser(userToEdit?.email)
        : await usersService.fetchUser(userToEdit?.email);

      if (userByEmail) {
        const groups = await userGroupService.getUserGroups({
          emails: [userToEdit?.email],
          isAdmin: isAdminUser
        });
        const userData = {
          ...userUtil.formatUsersDates(userByEmail),
          groupAssignments: groups.map((x) => x.targetGroupId)
        };

        setUser(userData);
        saveUserGroups(userByEmail);
      }
    } catch (err) {
      log.error("EditUserPage: Failed to fetch user", err);
      notificationService.showError({
        title: t(strCode.FETCH_USER_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMarkets = useCallback(async () => {
    dispatch(setMarketsError(null));

    if (isMarketsLoading) {
      return;
    }

    dispatch(setMarketsLoading(true));

    try {
      dispatch(resetMarkets());
      await marketsService.fetchMarkets();
    } catch (err) {
      dispatch(setMarketsError(err));
      log.error("EditUserPage: Failed to fetch markets", err);
      notificationService.showError({
        title: t(strCode.FETCH_MARKETS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setMarketsLoading(false));
    }
  }, []);

  const fetchAppTypes = useCallback(async () => {
    setIsAppTypesLoading(true);

    try {
      await authService.setAppTypes();

      const appTypes = selectAppTypesGroups()(getState());

      await Promise.all(
        appTypes
          .filter((appType) => appType !== appTypesNames.allApps)
          .map((appType) => groupsService.fetchGroup(appType.appType))
      );
    } catch (err) {
      log.error("EditUserPage: Failed to fetch apps", err);
      notificationService.showError({
        title: t(strCode.FETCH_APPS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setIsAppTypesLoading(false);
    }
  }, []);

  const fetchUserChangeHistory = useCallback(async () => {
    setChangeHistoryLoading(true);

    try {
      const changeHistory = await userChangeHistoryService.fetchUserChangeHistory(
        userToEdit?.email,
        isAdminUser
      );

      setUserChangeHistory(changeHistory);
    } catch (err) {
      log.error("EditUserPage: Failed to fetch user change history", err);
      notificationService.showError({
        title: t(strCode.HISTORY_FETCH_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setChangeHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarkets();
    fetchAppTypes();
    fetchUserChangeHistory();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (refresh) {
      fetchGroups(true);
    }
  }, [refresh]);

  const onEditUser = async (userData, groupAssignments) => {
    try {
      const res = isAdminUser
        ? await adminUserService.updateAdminUser(userData)
        : await usersService.updateUser(userData);

      await assignGroups(userData.email, user?.groupAssignments, groupAssignments, isAdminUser);

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.UPDATE_USER_SUCCESS),
          message: `${t(strCode.UPDATE_USER_SUCCESS_INFO)} <b>${user.email}</b>`
        });

        dispatch(setUsersRefresh(true));
        history.push(routePaths.USERS);
      }
    } catch (err) {
      log.error("EditUserPage: Failed to update user", err);
      notificationService.showError({
        title: t(strCode.UPDATE_USER_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      throw err;
    }
  };

  // Save user groups to global state to avoid bugs with roles if app user not refresh the page
  const saveUserGroups = (user) => {
    const groupsConfigs = selectGroupsConfigs()(getState());

    if (!adminUserUtils.isUserAdmin(user)) {
      const userGroups = user.groups.reduce((accum, curr) => {
        const result = { ...accum };

        if (curr.appType in result) {
          result[curr.appType].push(curr.name);
        } else {
          result[curr.appType] = [curr.name];
        }

        return result;
      }, {});

      Object.keys(userGroups).forEach((appType) => {
        dispatch(
          setGroupsConfigs({
            appType,
            groups: [...new Set([...userGroups[appType], ...(groupsConfigs[appType] || [])])]
          })
        );
      });
    }
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      {loading || isMarketsLoading || isAppTypesLoading ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
        </WrapperContainer>
      ) : !user && !loading && !isAppTypesLoading ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          gap={16}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.USER_FETCH_ERROR)}</Paragraph2>
          <BackLink to="/users">{t(strCode.USERS_BACK_BTN)}</BackLink>
        </WrapperContainer>
      ) : (
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          width="100%"
          height="100%">
          <WrapperContainer
            Container={FlexContainers.FlexRowBetween}
            alignItems={alignItems.start}
            width="100%">
            <WrapperContainer
              Container={FlexContainers.FlexColumnStart}
              alignItems={alignItems.start}
              width="100%">
              <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16} width={"100%"}>
                <Heading4>
                  {user?.firstName} {user?.lastName}
                </Heading4>
                <WrapperContainer>
                  <StatusIndicator
                    status={
                      userUtil.getIsActive(user)
                        ? tableDataStatuses.active
                        : tableDataStatuses.inactive
                    }
                  />
                </WrapperContainer>
              </WrapperContainer>

              <UserLoginInfoTemplate
                email={user.email}
                createdAtTs={user?.createdAtTs}
                lastLoginTs={user?.lastLoginTs}
              />
            </WrapperContainer>

            <DeleteUser
              email={user?.email}
              isAdminUser={adminUserUtils.isUserAdmin(user)}
              onDelete={() => deleteUser(user, isAdminUser, onEditUser)}
              onResetPassword={() => resetPassword(user?.email)}
            />
          </WrapperContainer>

          <NewUserForm
            isAdminUser={isAdminUser}
            userLoading={userLoading}
            usersData={user}
            changeHistory={userChangeHistory}
            changeHistoryLoading={changeHistoryLoading}
            onSaveUser={onEditUser}
            setGroupSettingsModalActive={setGroupSettingsModalActive}
          />

          <UserGroupsModal
            isActive={groupSettingsModalActive}
            setActive={setGroupSettingsModalActive}
            onSubmit={saveGroups}
          />
        </WrapperContainer>
      )}
    </PageContainer>
  );
};

EditUserPage.propTypes = {
  isAdminUser: PropTypes.bool
};

export default withSection(EditUserPage);
